/**
 * The preference model, and the two translations either side of it.
 *
 * Its own module so `vitest.config.ts` -- which runs in node with no DOM -- can
 * reach the mapping. The provider next door holds the state and the request;
 * nothing here touches either.
 */

export interface UserPreferences {
  language: string;
  region: string;
  measurementSystem: string;
  timezone: string;
}

/**
 * Used when the user has no settings stored, when the endpoint answers 404,
 * and on the first render before the load returns.
 */
export const defaultPreferences: UserPreferences = {
  language: 'en-US',
  region: 'en-US',
  measurementSystem: 'Metric',
  timezone: 'UTC',
};

/** Short codes stored before locales were written in full. */
const LOCALE_MIGRATION: Record<string, string> = {
  nl: 'nl-NL',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT',
};

const migrateLocale = (value: string): string =>
  LOCALE_MIGRATION[value] ?? value;

/** Where i18next keeps the language this device last showed. */
export const DEVICE_LANGUAGE_KEY = 'i18nextLng';

/**
 * The language this device last showed, so the screens before sign-in are in
 * it: the account's language replaces it once it loads, and the default stands
 * in when neither has one. Undefined when the device holds none of `shipped`,
 * or its storage is missing or throws.
 */
export const deviceLanguage = (
  storage: { getItem: (key: string) => string | null } | undefined,
  shipped: readonly string[],
): string | undefined => {
  try {
    const stored = storage?.getItem(DEVICE_LANGUAGE_KEY);
    const language = stored ? migrateLocale(stored) : undefined;
    return language && shipped.includes(language) ? language : undefined;
  } catch {
    return undefined;
  }
};

/**
 * What the account endpoint sent, as preferences.
 *
 * Only the four fields we know, so an unexpected property on the response
 * cannot end up in state; and only the ones actually present, so a missing
 * field keeps its default rather than becoming undefined.
 */
export const preferencesFromApi = (
  settings: Record<string, unknown> | null | undefined,
): Partial<UserPreferences> => {
  const mapped: Partial<UserPreferences> = {};
  if (!settings) return mapped;

  const { language, regionalSettings, systemOfMeasurement, timezone } =
    settings as Record<string, string | undefined>;

  if (language) mapped.language = migrateLocale(language);
  if (regionalSettings) mapped.region = migrateLocale(regionalSettings);
  if (systemOfMeasurement) mapped.measurementSystem = systemOfMeasurement;
  if (timezone) mapped.timezone = timezone;

  return mapped;
};

/** The body `PUT /users/me` takes. The names differ from ours on purpose. */
export const settingsPayload = (preferences: UserPreferences) => ({
  language: preferences.language,
  regionalSettings: preferences.region,
  systemOfMeasurement: preferences.measurementSystem,
  timezone: preferences.timezone,
});
